import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Button,
  styled,
  CircularProgress,
  Input,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import WallpaperOutlinedIcon from '@mui/icons-material/WallpaperOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useApiMutation } from '@/hooks/useApi';
export interface FeedbackFormData {
  reasons: string[];
  comment: string;
  files: File[];
  unSafeFlag: boolean;
}

interface Props {
  setSnackbar: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
    }>
  >;
  onClose: () => void;
  userId: string;
  messageId: string;
  disabledSubmitButton: boolean;
}
const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'application/pdf',
  'text/plain',
];
const REASON_KEYS = [
  'incorrect_info',
  'not_addressed',
  'incomplete',
  'unclear',
  'unsafe',
];

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'isOffensive',
})<{ isOffensive?: boolean }>(({ theme, isOffensive }) => ({
  ...(isOffensive && {
    color: theme.palette.error.main,
    '& .MuiTypography-root': {
      color: theme.palette.error.main,
    },
  }),
}));

const FeedbackFormContainer: React.FC<Props> = ({
  setSnackbar,
  userId,
  messageId,
  onClose,
  disabledSubmitButton,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['chat']);
  const [isUnSafe, setIsUnSafe] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState<string>('');
  const [commentError, setCommentError] = useState<boolean>(false);
  const [fileError, setFileError] = useState<{
    type: boolean;
    message: string;
  }>({
    type: false,
    message: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const { mutateAsync: sendFeedbackForm } = useApiMutation<void, any>({
    url: '/user/feedback/agent-response',
    method: 'post',
    options: { retry: 1 },
  });
  const REASONS_OPTIONS = REASON_KEYS.map((key) => ({
    id: key,
    label: t(`feedbackForm.reasons.${key}`),
  }));

  useEffect(() => {
    setSubmitEnabled(selectedReasons.length > 0);
  }, [selectedReasons]);
  const fileErrorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (fileErrorTimeoutRef.current) {
        clearTimeout(fileErrorTimeoutRef.current);
      }
    };
  }, []);
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedReasons((prev) =>
      checked ? [...prev, name] : prev.filter((reason) => reason !== name),
    );
  };

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
    setCommentError(event.target.value.length > 1000);
  };

  const processFiles = (inputFiles: FileList | File[]) => {
    const incomingFiles = Array.from(inputFiles);

    // 🚫 Reject all if max already reached
    if (files.length >= MAX_FILES) {
      if (fileErrorTimeoutRef.current) {
        clearTimeout(fileErrorTimeoutRef.current);
      }

      setFileError({
        type: true,
        message: t('feedbackForm.tooManyFiles'),
      });

      fileErrorTimeoutRef.current = setTimeout(() => {
        setFileError({ type: false, message: '' });
      }, 3000);

      return;
    }

    const validFiles: File[] = [];
    let errorMessage = '';

    for (const file of incomingFiles) {
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;
      const isValidType = ACCEPTED_TYPES.includes(file.type);

      if (!isValidType) {
        errorMessage = t('feedbackForm.invalidType');
        continue;
      }

      if (!isValidSize) {
        errorMessage = t('feedbackForm.fileTooLarge');
        continue;
      }

      if (validFiles.length + files.length < MAX_FILES) {
        validFiles.push(file);
      } else {
        errorMessage = t('feedbackForm.tooManyFiles');
      }
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }

    if (errorMessage) {
      if (fileErrorTimeoutRef.current) {
        clearTimeout(fileErrorTimeoutRef.current);
      }

      setFileError({ type: true, message: errorMessage });

      fileErrorTimeoutRef.current = setTimeout(() => {
        setFileError({ type: false, message: '' });
      }, 3000);
    }
  };
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      processFiles(event.target.files);
    }
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files) {
      processFiles(event.dataTransfer.files);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!submitEnabled) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('messageId', messageId);
      formData.append('userId', userId);
      formData.append('comment', comment);
      formData.append('unSafeFlag', String(isUnSafe));
      formData.append('reasons', JSON.stringify(selectedReasons));
      for (const file of files) formData.append('files', file);
      await sendFeedbackForm(formData);
      setSnackbar({
        open: true,
        message: t('feedbackForm.confirmationTitle'),
      });
    } catch (error) {
      console.error('Feedback submission failed in form:', error);
      setSnackbar({
        open: true,
        message: 'Feedback submission failed in form.',
      });
    } finally {
      onClose();
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedReasons([]);
    setComment('');
    setFiles([]);
    onClose();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      data-testid="feedback-form"
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        my: 2,
        width: '100%',
        boxShadow: 1,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          data-testid="feedback-form-title"
        >
          {t('feedbackForm.title')}
        </Typography>
        <IconButton
          onClick={handleCancel}
          aria-label={t('feedbackForm.closeAriaLabel')}
          size="small"
          data-testid="feedback-form-close-button"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography
        variant="body2"
        color="textSecondary"
        gutterBottom
        data-testid="feedback-form-subtitle"
      >
        {t('feedbackForm.whatWentWrong')}
      </Typography>

      <FormGroup sx={{ mb: 1 }} data-testid="feedback-reasons-group">
        {REASONS_OPTIONS.map((reason) => (
          <StyledFormControlLabel
            key={reason.id}
            control={
              <Checkbox
                checked={selectedReasons.includes(reason.id)}
                onChange={handleCheckboxChange}
                name={reason.id}
                size="small"
                aria-labelledby={`reason-label-${reason.id}`}
                data-testid={`reason-checkbox-${reason.id}`}
              />
            }
            label={
              <Typography variant="body2" id={`reason-label-${reason.id}`}>
                {reason.label}
              </Typography>
            }
            isOffensive={reason.id === 'unsafe'}
          />
        ))}
      </FormGroup>
      <Box>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('feedbackForm.commentPlaceholder')}
        </Typography>
        <TextField
          error={commentError}
          placeholder={t('feedbackForm.commentLabel')}
          multiline
          rows={3}
          value={comment}
          onChange={handleCommentChange}
          variant="outlined"
          fullWidth
          helperText={commentError && t('feedbackForm.commentError')}
          data-testid="feedback-comment-field"
        />
      </Box>

      <Box mt={2}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          data-testid="file-upload-title"
        >
          {t('feedbackForm.fileUploadTitle')}
        </Typography>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="file-drop-zone"
          sx={{
            borderWidth: '1px',
            borderStyle: 'dashed',
            borderColor: isDragging ? 'primary.main' : 'InactiveCaptionText',
            borderRadius: '2px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 1,
            gap: 0.5,
            ':hover': {
              borderColor: 'primary.main',
            },
            backgroundColor: isDragging
              ? theme.palette.action.hover
              : theme.palette.background.paper,
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          <FileUploadOutlinedIcon color="action" />
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              {t('feedbackForm.dragDropTitle')}
            </Typography>
            <label
              htmlFor="file-upload-input-feedback"
              style={{ cursor: 'pointer' }}
            >
              <Typography variant="body2" color="info">
                {t('feedbackForm.browse')}
              </Typography>
            </label>
          </Box>
          <Typography color="textDisabled" variant="caption">
            {t('feedbackForm.fileUploadDescription')}
          </Typography>
        </Box>

        <Input
          error={true}
          key={files.toString()}
          type="file"
          id="file-upload-input-feedback"
          inputProps={{
            multiple: true,
            accept: '.png,.jpg,.pdf,.txt',
            'aria-label': t('feedbackForm.fileUploadAriaLabel'),
          }}
          onChange={handleFileChange}
          sx={{ display: 'none' }}
          data-testid="file-upload-input"
        />
        {fileError.type && (
          <Typography
            variant="caption"
            color="error"
            data-testid="file-error-message"
          >
            {fileError.message}
          </Typography>
        )}

        {files.length > 0 && (
          <Box mt={1} data-testid="file-list">
            <Box component="ul" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
              {files.map((file, index) => (
                <Box
                  key={index}
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                    color: theme.palette.chatText.main,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(226, 227, 228, 0.2)'
                        : 'rgba(226, 227, 228, 0.5)',
                    paddingY: 0.5,
                    paddingX: 1,
                    borderRadius: '4px',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <WallpaperOutlinedIcon
                      style={{ fontSize: 14 }}
                      color="info"
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          margin: 0,
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        color="textDisabled"
                        variant="caption"
                        sx={{
                          display: 'block',
                          margin: 0,
                        }}
                      >
                        {(file.size / (1024 * 1024)).toFixed(2)} {'MB'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    aria-label="Remove file"
                    data-testid={`remove-file-${index}`}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {selectedReasons.includes('unsafe') && (
          <Box
            data-testid="unsafe-warning"
            sx={{
              marginY: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: 0.2,
              paddingX: 1,
              borderRadius: '4px',
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'red',
            }}
          >
            <Box display="flex" alignItems="center">
              <WarningAmberRoundedIcon color="error" />
              <Box>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={isUnSafe}
                    onChange={(e) => setIsUnSafe(e.target.checked)}
                    size="small"
                    aria-labelledby="unsafecheckbox"
                    data-testid="unsafe-flag-checkbox"
                    sx={{
                      color: 'red',
                      outlineWidth: 0.5,
                      '&.Mui-checked': {
                        color: 'red',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    color="error"
                    sx={{
                      display: 'block',
                      margin: 0,
                    }}
                  >
                    {t('feedbackForm.flagTitle')}
                  </Typography>
                </Box>
                <Typography
                  color="error"
                  variant="caption"
                  sx={{
                    display: 'block',
                    margin: 0,
                    paddingLeft: 1,
                  }}
                >
                  {t('feedbackForm.unsafeWarning')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
          data-testid="feedback-cancel-button"
        >
          {t('feedbackForm.cancelButton')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            !submitEnabled ||
            isSubmitting ||
            !disabledSubmitButton ||
            commentError
          }
          startIcon={
            isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{ borderRadius: '8px' }}
          data-testid="feedback-submit-button"
        >
          {isSubmitting
            ? t('feedbackForm.submittingButton')
            : t('feedbackForm.submitButtonDefault')}
        </Button>
      </Box>
    </Box>
  );
};

export default FeedbackFormContainer;
